//@ts-nocheck
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { ClickAwayListener } from '@mui/material';
import {
  SystemProps,
  PopoverContainer,
  PopoverContent,
  ChildrenContainer,
  PopoverKinds,
  Placement
} from './Popover.style';
import { useESCKey } from '../../utils/hooks';

/**
 * Accessibility-specific props for the popover.
 *
 * @see https://accessibility.athena-ict.com/aria/examples/tooltip.shtml
 */
export interface IPopoverA11yProps {
  /**
   * Unique DOM id for the popover. Used to hint to screen readers that the
   * provided children are related to the popover.
   */
  id: string;
}

export interface IPopoverProps extends IPopoverA11yProps, SystemProps {
  /**
   * Contents of the Popover.
   */
  content: React.ReactElement;

  /**
   * Placement of the popover relative to the children of `Popover`.
   */
  placement: Placement;

  /**
   * Trigger event to open/close the Popover.
   *
   * @default 'click'
   */
  trigger?: 'click' | 'hover';

  /**
   * Stops Popover from opening when trigger is detected
   */
  disabled?: boolean;

  /**
   *  The kind of popover to render. Can be either a filled or outlined popover
   *
   * * @default "outlined"
   */
  kind?: PopoverKinds;

  children?: React.ReactNode;
}

const initialBoundingClientRect = {
  bottom: 0,
  height: 0,
  left: 0,
  right: 0,
  top: 0,
  width: 0
};

/**
 * General popover container for any element.
 */
const Popover: React.FC<IPopoverProps> = ({
  trigger = 'click',
  kind = 'outlined',
  placement,
  disabled,
  children,
  content,
  id,
  ...props
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const childrenRef = React.useRef<HTMLDivElement>(null);
  const [childrenDimensions, setChildrenDimensions] = React.useState<
    ClientRect
  >(initialBoundingClientRect);

  /**
   * Handler for children container click. If
   * `trigger` is set to 'click', then this will
   * toggle the Popover.
   */
  const handleChildrenClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ): void => {
    if (trigger === 'click' && !disabled) {
      if (childrenRef && childrenRef.current) {
        const dimensions = childrenRef.current.getBoundingClientRect();
        setChildrenDimensions(dimensions);
        setIsOpen(!isOpen);
        e.stopPropagation();
      }
    }
  };

  /**
   * Handler for user entering component with their mouse. If
   * `trigger` is set to 'hover', then this will open the Popover.
   */
  const handleMouseOver = (): void => {
    if (trigger === 'hover' && !disabled) {
      if (childrenRef && childrenRef.current) {
        const dimensions = childrenRef.current.getBoundingClientRect();
        setChildrenDimensions(dimensions);
        setIsOpen(true);
      }
    }
  };

  /**
   * Handler for user focusing the children with tab. If
   * `trigger` is set to 'hover', then this will open the Popover.
   */
  const handleFocus = (): void => {
    if (trigger === 'hover' && !disabled) {
      if (childrenRef && childrenRef.current) {
        const dimensions = childrenRef.current.getBoundingClientRect();
        setChildrenDimensions(dimensions);
        setIsOpen(true);
      }
    }
  };

  /**
   * Handler for user leaving component with their mouse. If
   * `trigger` is set to 'hover', then this will close the Popover.
   */
  const handleMouseOut = (): void => {
    if (trigger === 'hover') {
      setIsOpen(false);
    }
  };

  /**
   * Handler for user losing focus of the children with tab. If
   * `trigger` is set to 'hover', then this will close the Popover.
   */
  const handleBlur = (): void => {
    if (trigger === 'hover') {
      setIsOpen(false);
    }
  };

  /**
   * Close the popover when user hits `esc` key.
   */
  useESCKey(() => {
    setIsOpen(false);
  });

  /**
   * Close the popover when window is resized.
   */
  React.useEffect(() => {
    const handleResize = (): void => {
      setIsOpen(false);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  /**
   *Portal for Popover, consists of a positioning container, to allow caret z-index rendering (More info:
   *https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Positioning/Understanding_z_index/The_stacking_context),
   *and content div, which renders content and carets, both the top and bottom pieces rendered above and below content, respectively
   */
  return (
    <>
      {isOpen &&
        ReactDOM.createPortal(
          <ClickAwayListener onClickAway={() => setIsOpen(false)}>
            <PopoverContainer
              placement={placement}
              childrenDimensions={childrenDimensions}
              onMouseOver={handleMouseOver}
              aria-hidden={!isOpen}
              kind={kind}
              {...(props as any)}
            >
              <PopoverContent
                id={id}
                role="tooltip"
                kind={kind}
                placement={placement}
                {...(props as any)}
              >
                {content}
              </PopoverContent>
            </PopoverContainer>
          </ClickAwayListener>,
          document.body
        )}

      {/* Render children normally */}
      <ChildrenContainer
        ref={childrenRef}
        onClick={handleChildrenClick}
        onMouseOver={handleMouseOver}
        onMouseOut={handleMouseOut}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        {React.Children.map(children as React.ReactElement, child => {
          return React.cloneElement(child, {
            'aria-hidden': 'false',
            tabIndex: 0,
            style: { outline: 'none' }
          });
        })}
      </ChildrenContainer>
    </>
  );
};

export default Popover;
